import calculateWordCountScore from "lib/resume-scoring/content-details-sections/word-count";
import { OverallAnalysis, ResumeAnalysis } from "../resume-analyse-types";

const analyseOverallResume = (
  resume: ResumeAnalysis,
  overallAnalysis: OverallAnalysis
): OverallAnalysis => {
  return {
    isDateStructureConsistent: overallAnalysis?.isDateStructureConsistent,
    totalNumberOfWords: calculateWordCountScore(resume) || 0,
    totalNumberOfBulletPoints: countNumberOfBulletPoints(resume) || 0,
    repeatedPhrases: analyzeRepetitions(resume).repeatedPhrases,
    repeatedSentences: analyzeRepetitions(resume).repeatedSentences,
    repeatedActionVerbs: analyzeRepetitions(resume).repeatedActionVerbs,
  };
};

export { analyseOverallResume };

function getSentences(text: string): string[] {
  return text
    .split(/[.!?]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

// Utility to generate n-grams (phrases of n words)
function getPhrases(text: string, minWords = 3, maxWords = 6): string[] {
  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .split(/\s+/)
    .filter(Boolean);
  const phrases: string[] = [];

  for (let n = minWords; n <= maxWords; n++) {
    for (let i = 0; i <= words.length - n; i++) {
      phrases.push(words.slice(i, i + n).join(' '));
    }
  }

  // Filter out common phrases that shouldn't be considered repetitions
  return phrases.filter(phrase => !isCommonPhrase(phrase));
}

// Function to check if a phrase is too common to be considered a repetition
function isCommonPhrase(phrase: string): boolean {
  // List of common phrases in resumes that shouldn't be flagged as repetitions
  const commonPhrases = [
    'responsible for', 'worked with a team', 'worked on a team', 
    'responsible for managing', 'worked with cross functional', 
    'worked with cross-functional', 'collaborated with team',
    'participated in the', 'participated in a', 'experience in the',
    'experience with the', 'knowledge of the', 'familiar with the',
    'as part of the', 'as part of a', 'as a member of'
  ];
  
  return commonPhrases.some(commonPhrase => 
    phrase.includes(commonPhrase) || 
    phrase.length < 12 // Very short phrases are often common
  );
}

interface RepetitionGroup {
  phrases: string[];
  count: number;
}

// Define and export the VerbGroup interface so it can be used elsewhere
export interface VerbGroup {
  verbs: string[];
  count: number;
  verbsWithCounts: Array<{verb: string, count: number}>;
}

const analyzeRepetitions = (
  resume: ResumeAnalysis
): {
  repeatedPhrases: RepetitionGroup;
  repeatedSentences: RepetitionGroup;
  repeatedActionVerbs: VerbGroup;
} => {
  const allDescriptions: string[] = [];

  // Add null checks for all sections
  if (resume.workExperiences?.individualWorkExperiencesAnalysis) {
    resume.workExperiences.individualWorkExperiencesAnalysis.forEach(w =>
      w.descriptionAnalysis?.forEach(d => {
        if (d.statement) allDescriptions.push(d.statement);
      })
    );
  }
  
  if (resume.educations?.individualEducationsAnalysis) {
    resume.educations.individualEducationsAnalysis.forEach(e =>
      e.descriptionAnalysis?.forEach(d => {
        if (d.statement) allDescriptions.push(d.statement);
      })
    );
  }
  
  if (resume.projects?.individualProjectsAnalysis) {
    resume.projects.individualProjectsAnalysis.forEach(p =>
      p.descriptionAnalysis?.forEach(d => {
        if (d.statement) allDescriptions.push(d.statement);
      })
    );
  }
  
  if (resume.skills?.descriptionAnalysis) {
    resume.skills.descriptionAnalysis.forEach(s => {
      if (s.statement) allDescriptions.push(s.statement);
    });
  }
  
  if (resume.certifications?.individualCertificatesAnalysis) {
    resume.certifications.individualCertificatesAnalysis.forEach(c =>
      c.descriptionAnalysis?.forEach(d => {
        if (d.statement) allDescriptions.push(d.statement);
      })
    );
  }
  
  if (resume.languages?.individualLanguagesAnalysis) {
    resume.languages.individualLanguagesAnalysis.forEach(l =>
      l.descriptionAnalysis?.forEach(d => {
        if (d.statement) allDescriptions.push(d.statement);
      })
    );
  }
  // Check awards if available
  if (resume.awards?.individualAwardsAnalysis) {
    resume.awards.individualAwardsAnalysis.forEach(a => {
      if (a.descriptionAnalysis) {
        a.descriptionAnalysis.forEach(d => {
          if (d.statement) allDescriptions.push(d.statement);
        });
      }
    });
  }
  
  // Check custom sections if available
  if (resume.custom?.descriptionAnalysis) {
    resume.custom.descriptionAnalysis.forEach(c => {
      if (c.statement) allDescriptions.push(c.statement);
    });
  }

  const sentenceMap = new Map<string, number>();
  const phraseMap = new Map<string, number>();
  const verbMap = new Map<string, number>();

  for (const desc of allDescriptions) {
    // Analyze sentences
    const sentences = getSentences(desc);
    for (const sentence of sentences) {
      const normalized = sentence.trim().toLowerCase();
      sentenceMap.set(normalized, (sentenceMap.get(normalized) || 0) + 1);
    }

    // Analyze phrases
    const phrases = getPhrases(desc);
    for (const phrase of phrases) {
      phraseMap.set(phrase, (phraseMap.get(phrase) || 0) + 1);
    }
  }

  // Analyze verbs across all sections
  // Work experiences
  if (resume.workExperiences?.individualWorkExperiencesAnalysis) {
    resume.workExperiences.individualWorkExperiencesAnalysis.forEach(w => {
      if (w.descriptionAnalysis) {
        w.descriptionAnalysis.forEach(d => {
          if (d.analysis?.actionVerbAnalysis) {
            // Filter for first-word verbs only
            const statement = d.statement?.trim() || '';
            const firstWord = statement.split(' ')[0]?.toLowerCase().replace(/[^\w]/g, '') || '';
            
            d.analysis.actionVerbAnalysis.forEach(a => {
              const verb = a.verb.toLowerCase();
              // Only count the verb if it's the first word in the statement
              if (firstWord === verb) {
                verbMap.set(verb, (verbMap.get(verb) || 0) + 1);
              }
            });
          }
        });
      }
    });
  }
  
  // Education
  if (resume.educations?.individualEducationsAnalysis) {
    resume.educations.individualEducationsAnalysis.forEach(e => {
      if (e.descriptionAnalysis) {
        e.descriptionAnalysis.forEach(d => {
          if (d.analysis?.actionVerbAnalysis) {
            // Filter for first-word verbs only
            const statement = d.statement?.trim() || '';
            const firstWord = statement.split(' ')[0]?.toLowerCase().replace(/[^\w]/g, '') || '';
            
            d.analysis.actionVerbAnalysis.forEach(a => {
              const verb = a.verb.toLowerCase();
              // Only count the verb if it's the first word in the statement
              if (firstWord === verb) {
                verbMap.set(verb, (verbMap.get(verb) || 0) + 1);
              }
            });
          }
        });
      }
    });
  }
  
  // Projects
  if (resume.projects?.individualProjectsAnalysis) {
    resume.projects.individualProjectsAnalysis.forEach(p => {
      if (p.descriptionAnalysis) {
        p.descriptionAnalysis.forEach(d => {
          if (d.analysis?.actionVerbAnalysis) {
            // Filter for first-word verbs only
            const statement = d.statement?.trim() || '';
            const firstWord = statement.split(' ')[0]?.toLowerCase().replace(/[^\w]/g, '') || '';
            
            d.analysis.actionVerbAnalysis.forEach(a => {
              const verb = a.verb.toLowerCase();
              // Only count the verb if it's the first word in the statement
              if (firstWord === verb) {
                verbMap.set(verb, (verbMap.get(verb) || 0) + 1);
              }
            });
          }
        });
      }
    });
  }
  
  // Certifications
  if (resume.certifications?.individualCertificatesAnalysis) {
    resume.certifications.individualCertificatesAnalysis.forEach(c => {
      if (c.descriptionAnalysis) {
        c.descriptionAnalysis.forEach(d => {
          if (d.analysis?.actionVerbAnalysis) {
            // Filter for first-word verbs only
            const statement = d.statement?.trim() || '';
            const firstWord = statement.split(' ')[0]?.toLowerCase().replace(/[^\w]/g, '') || '';
            
            d.analysis.actionVerbAnalysis.forEach(a => {
              const verb = a.verb.toLowerCase();
              // Only count the verb if it's the first word in the statement
              if (firstWord === verb) {
                verbMap.set(verb, (verbMap.get(verb) || 0) + 1);
              }
            });
          }
        });
      }
    });
  }

  // Get top repeated sentences (filter out very short sentences and only count if repeated more than once)
  const topSentences = [...sentenceMap.entries()]
    .filter(([sentence, count]) => count > 1 && sentence.length > 15)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15); // Limit to top 15 repeated sentences to avoid overwhelming the user

  const repeatedSentences: RepetitionGroup = {
    phrases: topSentences.map(([sentence]) => sentence),
    count: topSentences.length,
  };

  // Get top repeated phrases
  const topPhrases = [...phraseMap.entries()]
    .filter(([phrase, count]) => count > 1)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15); // Limit to top 15 most repeated phrases

  const repeatedPhrases: RepetitionGroup = {
    phrases: topPhrases.map(([phrase]) => phrase),
    count: topPhrases.length,
  };

  // Get top repeated verbs (excluding very common verbs if they're not overused)
  const commonResumeVerbs = ['worked', 'developed', 'created', 'managed', 'led', 'implemented'];
  
  // Log the verb map to see actual counts
  // console.log("Verb counts:", Object.fromEntries(verbMap));
  
  // Only include verbs that appear multiple times (with different thresholds for common vs uncommon verbs)
  const filteredVerbEntries = [...verbMap.entries()]
    .filter(([verb, count]) => {
      // Allow common verbs only if they're significantly overused (more than 3 times)
      return commonResumeVerbs.includes(verb) ? count > 3 : count > 1;
    })
    .sort((a, b) => b[1] - a[1]) // Sort by count (descending)
    .slice(0, 10); // Limit to top 10 most repeated verbs
    
  // Create an array of objects with verb and count for more detailed info
  const verbsWithCounts = filteredVerbEntries.map(([verb, count]) => {
    return {
      verb: verb,
      count: count
    };
  });
  
  // console.log("Filtered verb entries:", verbsWithCounts);

  // Create object with properly calculated count and verbs list
  const repeatedActionVerbs: VerbGroup = {
    verbs: filteredVerbEntries.map(([verb]) => verb),
    count: filteredVerbEntries.length,
    verbsWithCounts: verbsWithCounts
  };

  return { repeatedSentences, repeatedPhrases, repeatedActionVerbs };

}


const countNumberOfBulletPoints = (resume: ResumeAnalysis): number => {
  let totalNumberOfBulletPoints = 0;
  // Work Experience
  if (resume?.workExperiences?.individualWorkExperiencesAnalysis) {
    resume?.workExperiences?.individualWorkExperiencesAnalysis.map(
      (workExperience) => {
        if (workExperience?.descriptionAnalysis) {
          totalNumberOfBulletPoints +=
            workExperience?.descriptionAnalysis.length;
        }
      }
    );
  }
  // projects
  if (resume?.projects?.individualProjectsAnalysis) {
    resume?.projects?.individualProjectsAnalysis.map((project) => {
      if (project?.descriptionAnalysis) {
        totalNumberOfBulletPoints += project?.descriptionAnalysis.length;
      }
    });
  }
  // educations
  if (resume?.educations?.individualEducationsAnalysis) {
    resume?.educations?.individualEducationsAnalysis.map((education) => {
      if (education?.descriptionAnalysis) {
        totalNumberOfBulletPoints += education?.descriptionAnalysis.length;
      }
    });
  }
  // certificates
  if (resume?.certifications?.individualCertificatesAnalysis) {
    resume?.certifications?.individualCertificatesAnalysis.map(
      (certificate) => {
        if (certificate?.descriptionAnalysis) {
          totalNumberOfBulletPoints += certificate?.descriptionAnalysis.length;
        }
      }
    );
  }
  // languages
  if (resume?.languages?.individualLanguagesAnalysis) {
    resume?.languages?.individualLanguagesAnalysis.map((language) => {
      if (language?.descriptionAnalysis) {
        totalNumberOfBulletPoints += language?.descriptionAnalysis.length;
      }
    });
  }
  // awards
  if (resume?.awards?.individualAwardsAnalysis) {
    resume?.awards?.individualAwardsAnalysis.map((award) => {
      if (award?.descriptionAnalysis) {
        totalNumberOfBulletPoints += award?.descriptionAnalysis.length;
      }
    });
  }
  // skills
  if (resume?.skills?.descriptionAnalysis) {
    totalNumberOfBulletPoints += resume?.skills?.descriptionAnalysis.length;
  }
  // custom
  if (resume?.custom?.descriptionAnalysis) {
    totalNumberOfBulletPoints += resume?.custom?.descriptionAnalysis.length;
  }

  return totalNumberOfBulletPoints;
};
